import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Check,
  Loader2,
  CreditCard,
  Crown,
  Building2,
  Sparkles,
  AlertTriangle,
  Users,
  Brain,
  FileText,
  Share2,
  BarChart3,
  Layers,
  HeadphonesIcon,
  Tag,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const PLAN_ICONS = {
  free: Sparkles,
  solo_vet: CreditCard,
  clinic: Building2,
  enterprise: Crown
};

const PLAN_COLORS = {
  free: '#5C5F5C',
  solo_vet: '#4B6651',
  clinic: '#C06144',
  enterprise: '#8B6914'
};

const FEATURE_LABELS = {
  basic_exam: { label: 'Neurological Exams', icon: Brain },
  pdf_export: { label: 'PDF Export', icon: FileText },
  patient_sharing: { label: 'Patient Sharing', icon: Share2 },
  analytics: { label: 'Analytics Dashboard', icon: BarChart3 },
  unlimited_patients: { label: 'Unlimited Patients', icon: Users },
  unlimited_analyses: { label: 'Unlimited AI Analyses', icon: Brain },
  multi_user: { label: 'Multi-User Access', icon: Users },
  clinic_management: { label: 'Clinic Management', icon: Building2 },
  bulk_dicom: { label: 'Bulk DICOM Import', icon: Layers },
  priority_support: { label: 'Priority Support', icon: HeadphonesIcon }
};

const Subscription = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [polling, setPolling] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscription/plans`),
        axios.get(`${BACKEND_URL}/api/subscription/current`, { withCredentials: true })
      ]);
      setPlans(plansRes.data.plans);
      setCurrentSub(subRes.data);
    } catch (error) {
      logger.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for payment status on return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    let attempts = 0;
    const maxAttempts = 10;

    const pollStatus = async () => {
      setPolling(true);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/subscription/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (data.payment_status === 'paid') {
          toast.success('Subscription activated! Welcome aboard.');
          setPolling(false);
          fetchData();
          window.history.replaceState({}, '', '/subscription');
          return;
        }

        if (data.status === 'expired') {
          toast.error('Payment session expired. Please try again.');
          setPolling(false);
          window.history.replaceState({}, '', '/subscription');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setPolling(false);
          toast.error('Payment status check timed out. Please refresh.');
          window.history.replaceState({}, '', '/subscription');
        }
      } catch (error) {
        setPolling(false);
        logger.error('Error polling status:', error);
      }
    };

    pollStatus();
  }, [searchParams, fetchData]);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const originUrl = window.location.origin;
      const { data } = await axios.post(
        `${BACKEND_URL}/api/subscription/checkout`,
        { plan_id: planId, origin_url: originUrl },
        { withCredentials: true }
      );
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  if (polling) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4" data-testid="payment-processing">
        <Loader2 className="w-12 h-12 animate-spin text-[#4B6651]" />
        <h2 className="text-xl font-semibold text-[#2A2C2A] font-['Work_Sans']">Processing Payment...</h2>
        <p className="text-[#5C5F5C]">Please wait while we confirm your subscription.</p>
      </div>
    );
  }

  const paidPlans = plans.filter(p => p.id !== 'free');

  return (
    <div className="space-y-8 animate-fade-in max-w-5xl mx-auto" data-testid="subscription-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">
          Subscription Plans
        </h1>
        <p className="text-[#5C5F5C] mt-2">
          Choose the plan that fits your practice
        </p>
      </div>

      {/* Current Plan Banner */}
      {currentSub && (
        <Card className={`border-[#4B6651] ${currentSub.is_owner ? 'bg-[#8B6914]/5 border-[#8B6914]' : 'bg-[#4B6651]/5'}`} data-testid="current-plan-banner">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm text-[#5C5F5C] font-medium">Current Plan</p>
                <p className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] flex items-center gap-2">
                  {currentSub.plan_name}
                  {currentSub.is_owner && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#8B6914]/15 text-[#8B6914] rounded text-xs font-medium">
                      <Shield className="w-3 h-3" />
                      Owner
                    </span>
                  )}
                  {currentSub.status === 'trialing' && (
                    <span className="px-2 py-0.5 bg-[#C06144]/10 text-[#C06144] rounded text-xs font-medium">
                      Trial
                    </span>
                  )}
                </p>
                {currentSub.is_owner && (
                  <p className="text-sm text-[#8B6914] mt-1">
                    Unlimited access — no subscription required
                  </p>
                )}
                {!currentSub.is_owner && currentSub.current_period_end && (
                  <p className="text-sm text-[#5C5F5C] mt-1">
                    {currentSub.status === 'trialing' ? 'Trial ends' : 'Renews'}: {new Date(currentSub.current_period_end).toLocaleDateString()}
                  </p>
                )}
              </div>
              {!currentSub.is_owner && (
                <div className="flex flex-col sm:items-end gap-1">
                  <p className="text-sm text-[#5C5F5C]">
                    Patients: <span className="font-medium text-[#2A2C2A]">{currentSub.usage?.patients || 0}</span>
                    {currentSub.max_patients > 0 && <span> / {currentSub.max_patients}</span>}
                  </p>
                  <p className="text-sm text-[#5C5F5C]">
                    AI Analyses this month: <span className="font-medium text-[#2A2C2A]">{currentSub.usage?.analyses_this_month || 0}</span>
                    {currentSub.max_ai_analyses_per_month > 0 && <span> / {currentSub.max_ai_analyses_per_month}</span>}
                  </p>
                </div>
              )}
            </div>

            {/* Limit warnings */}
            {!currentSub.is_owner && currentSub.max_patients > 0 && (currentSub.usage?.patients || 0) >= currentSub.max_patients && (
              <div className="mt-3 flex items-center gap-2 p-2 bg-[#C06144]/10 rounded text-sm text-[#C06144]">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Patient limit reached. Upgrade to add more patients.
              </div>
            )}
            {!currentSub.is_owner && currentSub.max_ai_analyses_per_month > 0 && (currentSub.usage?.analyses_this_month || 0) >= currentSub.max_ai_analyses_per_month && (
              <div className="mt-3 flex items-center gap-2 p-2 bg-[#C06144]/10 rounded text-sm text-[#C06144]">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                Monthly AI analysis limit reached. Upgrade for unlimited analyses.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="plans-grid">
        {paidPlans.map((plan) => {
          const Icon = PLAN_ICONS[plan.id] || CreditCard;
          const color = PLAN_COLORS[plan.id] || '#4B6651';
          const isCurrent = currentSub?.plan_id === plan.id;
          const isPopular = plan.id === 'solo_vet';

          return (
            <Card
              key={plan.id}
              className={`border-2 relative ${
                isCurrent ? 'border-[#4B6651] shadow-lg' :
                isPopular ? 'border-[#4B6651]/50 shadow-md' :
                'border-[#E5E4E0]'
              }`}
              data-testid={`plan-card-${plan.id}`}
            >
              {isPopular && !isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#4B6651] text-white text-xs font-medium rounded-full">
                  Most Popular
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#4B6651] text-white text-xs font-medium rounded-full">
                  Current Plan
                </div>
              )}
              <CardHeader className="text-center pb-2 pt-6">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3"
                  style={{ backgroundColor: `${color}15` }}
                >
                  <Icon className="w-6 h-6" style={{ color }} />
                </div>
                <CardTitle className="text-xl font-['Work_Sans']">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-[#2A2C2A]">${plan.price}</span>
                  <span className="text-[#5C5F5C]">/month</span>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => {
                    const featureInfo = FEATURE_LABELS[feature];
                    if (!featureInfo) return null;
                    const FeatureIcon = featureInfo.icon;
                    return (
                      <li key={feature} className="flex items-center gap-2 text-sm text-[#2A2C2A]">
                        <Check className="w-4 h-4 text-[#4B6651] flex-shrink-0" />
                        {featureInfo.label}
                      </li>
                    );
                  })}
                  {plan.max_patients > 0 && (
                    <li className="flex items-center gap-2 text-sm text-[#5C5F5C]">
                      <Check className="w-4 h-4 text-[#5C5F5C] flex-shrink-0" />
                      Up to {plan.max_patients} patients
                    </li>
                  )}
                </ul>

                <Button
                  className={`w-full ${
                    isCurrent
                      ? 'bg-[#E5E4E0] text-[#5C5F5C] cursor-default hover:bg-[#E5E4E0]'
                      : 'bg-[#4B6651] hover:bg-[#3E5543] text-white'
                  }`}
                  disabled={isCurrent || checkoutLoading === plan.id}
                  onClick={() => !isCurrent && handleCheckout(plan.id)}
                  data-testid={`checkout-${plan.id}`}
                >
                  {checkoutLoading === plan.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {isCurrent ? 'Current Plan' : `Subscribe — $${plan.price}/mo`}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Promo Code Info */}
      <Card className="border-[#E5E4E0] bg-[#F8F7F4]" data-testid="promo-code-info">
        <CardContent className="p-4 sm:p-6 flex items-start gap-3">
          <Tag className="w-5 h-5 text-[#4B6651] flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-[#2A2C2A]">Have a promo code?</p>
            <p className="text-sm text-[#5C5F5C] mt-0.5">
              You can enter your promotion code on the Stripe checkout page after clicking any Subscribe button above.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Free Tier Info */}
      <Card className="border-[#E5E4E0] bg-[#F8F7F4]" data-testid="free-tier-info">
        <CardContent className="p-6 text-center">
          <p className="text-sm text-[#5C5F5C]">
            All new accounts start with a <span className="font-medium text-[#2A2C2A]">14-day free trial</span> including 2 patients and 2 AI analyses per month.
            After the trial, you'll be downgraded to the free tier unless you subscribe.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscription;
